/* eslint-disable indent */
/* eslint-disable operator-linebreak */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import {
  CommonCurrencyInput,
  CommonDropdown,
  DatePicker,
  FullScreenLoading,
  RegularButton,
  TextAreaInput,
} from 'pg-ui-library';
import Arrow from 'src/assets/arrow-left-black.svg';
import {
  IBasedOnCoverageAmountAndAge,
  ICoverageAmountBased,
  IFixedCostPremium,
  IFixedPortionOfTheEmployeePremium,
  IPremiumPayoutConfig,
} from 'src/interfaces';
import {
  ArrowBack,
  Container,
  NavTextLink,
  PageTitle,
  RadioBody,
  RadioContainer,
  Row,
  RowItem,
  RowLabel,
  StyledRadioButton,
  StyledRadioContainer,
  StyledRadioGroup,
  StyledRadioInput,
  StyledRadioLabel,
  Subtitle,
} from './styles';
import { usePostBenefitPremiumPayout } from '../../mutations/post-benefit-premium-payout.mutation';

import { useBenefitPremiumPayout } from '../../queries/benefit-premium-payout.query';

const premiumValidationSchema = Yup.object().shape({
  id: Yup.string(),
  frequencyOfEmployeeBenefitPayments: Yup.string(),
  percentageOfEmployeeSalaryPaidByPlan: Yup.string(),
  roundPercentageOfsalary: Yup.string(),
  roundPercentageOfsalaryUpTo: Yup.mixed(),
  describeAnyWaitingPeriodDetails: Yup.string().max(255),
  describeAnyDetailsAboutBenefitDuration: Yup.string().max(255),
  premium: Yup.object().shape({
    costMethod: Yup.string(),
    employeeCost: Yup.mixed(),
    spouseCost: Yup.mixed(),
    childCost: Yup.mixed(),
    employeeCostTimes: Yup.mixed(),
    spouseCostTimes: Yup.mixed(),
    childCostTimes: Yup.mixed(),
    additionalAmountForSmokers: Yup.mixed(),
    additionalAmountForSmokersTimes: Yup.mixed(),
    openEnrollmentDate: Yup.string(),
    newHireQLEDate: Yup.string(),
    openEnrollmentDateOption: Yup.string(),
    newHireQLEDateOption: Yup.string(),
    useEmployeeAgeForSpousePremium: Yup.boolean(),
  }),
  payrollBenefitCode: Yup.string(),
  payrollDeductionCode: Yup.string(),
  employerProvidedQuantity: Yup.object().shape({
    employerAssistance: Yup.mixed(),
  }),
});

const initialPremiumPayoutConfig: Partial<IPremiumPayoutConfig> = {
  benefitType: '',
  frequencyOfEmployeeBenefitPayments: '',
  percentageOfEmployeeSalaryPaidByPlan: '',
  roundPercentageOfsalary: '',
  roundPercentageOfsalaryUpTo: '',
  describeAnyWaitingPeriodDetails: '',
  describeAnyDetailsAboutBenefitDuration: '',
  premiumType: 'fixedCost',
  premium: {
    costMethod: 'Fixed Amount',
    employeeCost: '',
    spouseCost: '',
    childCost: '',
    employeeCostTimes: '',
    spouseCostTimes: '',
    childCostTimes: '',
    additionalAmountForSmokers: '',
    additionalAmountForSmokersTimes: '',
    openEnrollmentDate: '',
    newHireQLEDate: '',
    openEnrollmentDateOption: 'Custom Date',
    newHireQLEDateOption: 'Custom Date',
    useEmployeeAgeForSpousePremium: false,
  },
  payrollBenefitCode: '',
  payrollDeductionCode: '',
  employerProvidedQuantityType: 'fixed',
  employerProvidedQuantity: {
    employerAssistance: '',
  },
};

const roundDropdownOptions = [
  {
    key: 'Round',
    label: 'Round',
    value: 'Round',
  },
  {
    key: 'No rouding',
    label: 'No Rouding',
    value: 'No Rouding',
  },
  {
    key: 'Round Up',
    label: 'Round Up',
    value: 'Round Up',
  },
  {
    key: 'Round Down',
    label: 'Round Down',
    value: 'Round Down',
  },
];

const frequencyDropdownOptions = [
  {
    key: 'Weekly',
    label: 'Weekly',
    value: 'Weekly',
  },
  {
    key: 'Monthly',
    label: 'Monthly',
    value: 'Monthly',
  },
];

const costDropdownOptions = [
  {
    key: 'Fixed Amount',
    label: 'Fixed Amount',
    value: 'Fixed Amount',
  },
];

const openEnrollmentDateDropdownOptions = [
  {
    key: 'Custom Date',
    label: 'Custom Date',
    value: 'Custom Date',
  },
  {
    key: 'Last Birthday',
    label: 'Last Birthday',
    value: 'Last Birthday',
  },
  {
    key: 'Next Birthday',
    label: 'Next Birthday',
    value: 'Next Birthday',
  },
  {
    key: 'Start of Year',
    label: 'Start of Year',
    value: 'Start of Year',
  },
  {
    key: 'End of Year',
    label: 'End of Year',
    value: 'End of Year',
  },
  {
    key: 'Plan Period Start Date',
    label: 'Plan Period Start Date',
    value: 'Plan Period Start Date',
  },
  {
    key: 'Plan Period End Date',
    label: 'Plan Period End Date',
    value: 'Plan Period End Date',
  },
  {
    key: 'Date of Election',
    label: 'Date of Election',
    value: 'Date of Election',
  },
];

const qleDateDropdownOptions = [
  {
    key: 'Custom Date',
    label: 'Custom Date',
    value: 'Custom Date',
  },
  {
    key: 'Last Birthday',
    label: 'Last Birthday',
    value: 'Last Birthday',
  },
  {
    key: 'Next Birthday',
    label: 'Next Birthday',
    value: 'Next Birthday',
  },
  {
    key: 'Start of Year',
    label: 'Start of Year',
    value: 'Start of Year',
  },
  {
    key: 'End of Year',
    label: 'End of Year',
    value: 'End of Year',
  },
  {
    key: 'Date of Election',
    label: 'Date of Election',
    value: 'Date of Election',
  },
];

export const BenefitPremiumPayoutConfig = () => {
  const { t } = useTranslation();

  const styleError = (error: string) => ({ border: error ? '1px solid red' : '' });

  const { versionId, benefitType } = useParams();

  const post = usePostBenefitPremiumPayout();

  const {
    data: response,
    isFetching: serverLoading,
    refetch,
  } = useBenefitPremiumPayout({ versionId: versionId || '' });

  const isLoading = post.isLoading || serverLoading;

  useEffect(() => {
    refetch();
  }, [versionId]);

  const formik = useFormik({
    initialValues: response?.data.id
      ? response?.data
      : ({
          ...initialPremiumPayoutConfig,
          benefitType,
          versionId: versionId || '',
        } as IPremiumPayoutConfig),
    validationSchema: premiumValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values: IPremiumPayoutConfig) => {
      post.mutateAsync(values);
    },
  });

  if (serverLoading) {
    return <FullScreenLoading />;
  }

  const handleSelectedDate = (value: string | Date) => {
    if (value === '') {
      return null;
    }
    if (typeof value === 'string') {
      return new Date(value);
    }

    return value;
  };

  return (
    <Container>
      <Row style={{ justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
          <NavTextLink to={-1 as any}>
            <ArrowBack src={Arrow} />
          </NavTextLink>
          <PageTitle>{t('benefits.premiumConfig.premiumAndPayoutConfig')}</PageTitle>
        </div>
        <RegularButton onClick={() => formik.handleSubmit()} type="submit" disabled={isLoading}>
          {t('global.form.save')}
        </RegularButton>
      </Row>
      <Row>
        <RowItem>
          <CommonDropdown
            label="Frequency of employee benefit payments"
            id="frequencyOfEmployeeBenefitPayments"
            name="frequencyOfEmployeeBenefitPayments"
            setValue={(value: string) => {
              formik.setFieldValue('frequencyOfEmployeeBenefitPayments', value);
            }}
            value={formik.values.frequencyOfEmployeeBenefitPayments}
            options={frequencyDropdownOptions || []}
            selectStyle={{
              maxWidth: 'none',
              width: '100%',
              backgroundColor: '#ffffff',
              height: '35px',
              marginTop: '8px',
            }}
          />
        </RowItem>
        <RowItem>
          <CommonCurrencyInput
            id="percentageOfEmployeeSalaryPaidByPlan"
            label="Percentage of the employee’s salary paid by plan"
            name="percentageOfEmployeeSalaryPaidByPlan"
            placeholder="Please enter a number"
            suffix="%"
            allowDecimals={false}
            allowNegativeValue={false}
            value={formik.values.percentageOfEmployeeSalaryPaidByPlan}
            onValueChange={v => {
              if (Number.isNaN(v)) {
                formik.setFieldValue('percentageOfEmployeeSalaryPaidByPlan', 0);
              } else {
                formik.setFieldValue('percentageOfEmployeeSalaryPaidByPlan', v);
              }
            }}
          />
        </RowItem>
        <RowItem>
          <CommonDropdown
            label="Round percentage of salary"
            id="roundPercentageOfsalary"
            name="roundPercentageOfsalary"
            setValue={(value: string) => {
              formik.setFieldValue('roundPercentageOfsalary', value);
            }}
            value={formik.values.roundPercentageOfsalary}
            options={roundDropdownOptions || []}
            selectStyle={{
              maxWidth: 'none',
              width: '100%',
              backgroundColor: '#ffffff',
              height: '35px',
              marginTop: '8px',
            }}
          />
        </RowItem>
        <span style={{ alignSelf: 'end', marginBottom: '8px' }}>up to</span>
        <RowItem>
          <CommonCurrencyInput
            id="roundPercentageOfsalaryUpTo"
            label=""
            name="roundPercentageOfsalaryUpTo"
            placeholder="Please enter a number"
            prefix="$"
            decimalsLimit={7}
            allowNegativeValue={false}
            value={formik.values.roundPercentageOfsalaryUpTo}
            onValueChange={v => {
              if (Number.isNaN(v)) {
                formik.setFieldValue('roundPercentageOfsalaryUpTo', 0);
              } else {
                formik.setFieldValue('roundPercentageOfsalaryUpTo', v);
              }
            }}
          />
        </RowItem>
      </Row>
      <Row>
        <div>
          <TextAreaInput
            id="describeAnyWaitingPeriodDetails"
            name="describeAnyWaitingPeriodDetails"
            label="Describe any waiting period details"
            value={formik.values.describeAnyWaitingPeriodDetails}
            onChange={e => formik.setFieldValue('describeAnyWaitingPeriodDetails', e.target.value)}
            placeholder="type here..."
            rows={10}
            cols={80}
            style={styleError(formik.errors.describeAnyWaitingPeriodDetails || '')}
          />
        </div>
        <TextAreaInput
          id="describeAnyDetailsAboutBenefitDuration"
          name="describeAnyDetailsAboutBenefitDuration"
          label="Describe any details about benefit duration"
          value={formik.values.describeAnyDetailsAboutBenefitDuration}
          onChange={e => formik.setFieldValue('describeAnyDetailsAboutBenefitDuration', e.target.value)}
          placeholder="type here..."
          rows={10}
          cols={80}
          style={styleError(formik.errors.describeAnyWaitingPeriodDetails || '')}
        />
      </Row>

      <Subtitle style={{ margin: 0 }}>{t('benefits.premiumConfig.premium')}</Subtitle>

      <Row>
        <RadioBody>
          <RadioContainer>
            <input
              type="radio"
              id="premium"
              name="premium"
              value="fixedCost"
              checked={formik.values.premiumType === 'fixedCost'}
              onChange={e => {
                formik.setFieldValue('premiumType', e.target.value);
              }}
            />
            <label>{t('benefits.premiumConfig.fixedCostPremium')}</label>
          </RadioContainer>
          {formik.values.premiumType === 'fixedCost' && (
            <>
              <Row>
                <RowItem>
                  <CommonDropdown
                    label="Cost Method"
                    id="costMethod"
                    name="costMethod"
                    setValue={(value: string) => {
                      formik.setFieldValue('premium.costMethod', value);
                    }}
                    value={(formik.values.premium as IFixedCostPremium).costMethod}
                    options={costDropdownOptions || []}
                    selectStyle={{
                      maxWidth: 'none',
                      width: '100%',
                      backgroundColor: '#ffffff',
                      height: '35px',
                      marginTop: '8px',
                    }}
                  />
                </RowItem>
                <RowItem>
                  <CommonCurrencyInput
                    id="employeeCost"
                    label="Employee Cost"
                    name="employeeCost"
                    placeholder="Please enter a number"
                    prefix="$"
                    decimalsLimit={7}
                    allowNegativeValue={false}
                    value={(formik.values.premium as IFixedCostPremium).employeeCost}
                    onValueChange={v => {
                      if (Number.isNaN(v)) {
                        formik.setFieldValue('premium.employeeCost', 0);
                      } else {
                        formik.setFieldValue('premium.employeeCost', v);
                      }
                    }}
                  />
                </RowItem>
              </Row>
              <Row>
                <RowItem>
                  <CommonCurrencyInput
                    id="spouseCost"
                    label="Spouse Cost"
                    name="spouseCost"
                    placeholder="Please enter a number"
                    prefix="$"
                    decimalsLimit={7}
                    allowNegativeValue={false}
                    value={(formik.values.premium as IFixedCostPremium).spouseCost}
                    onValueChange={v => {
                      if (Number.isNaN(v)) {
                        formik.setFieldValue('premium.spouseCost', 0);
                      } else {
                        formik.setFieldValue('premium.spouseCost', v);
                      }
                    }}
                  />
                </RowItem>
                <RowItem>
                  <CommonCurrencyInput
                    id="childCost"
                    label="Child(red) Cost"
                    name="childCost"
                    placeholder="Please enter a number"
                    prefix="$"
                    decimalsLimit={7}
                    allowNegativeValue={false}
                    value={(formik.values.premium as IFixedCostPremium).childCost}
                    onValueChange={v => {
                      if (Number.isNaN(v)) {
                        formik.setFieldValue('premium.childCost', 0);
                      } else {
                        formik.setFieldValue('premium.childCost', v);
                      }
                    }}
                  />
                </RowItem>
              </Row>
            </>
          )}
          <RadioContainer>
            <input
              type="radio"
              id="premium"
              name="premium"
              value="coverageAmountBased"
              checked={formik.values.premiumType === 'coverageAmountBased'}
              onChange={e => {
                formik.setFieldValue('premiumType', e.target.value);
              }}
            />
            <label>{t('benefits.premiumConfig.coverageAmountBased')}</label>
          </RadioContainer>
          {formik.values.premiumType === 'coverageAmountBased' && (
            <>
              <Row>
                <RowLabel>
                  <Subtitle>{t('benefits.premiumConfig.employeeCost')}</Subtitle>
                  <span>{t('benefits.premiumConfig.coverageAmoundDivided')}</span>
                </RowLabel>
                <RowItem>
                  <CommonCurrencyInput
                    id="employeeCost"
                    label=""
                    name="employeeCost"
                    placeholder="Please enter a number"
                    prefix="$"
                    decimalsLimit={7}
                    allowNegativeValue={false}
                    value={(formik.values.premium as ICoverageAmountBased).employeeCost}
                    onValueChange={v => {
                      if (Number.isNaN(v)) {
                        formik.setFieldValue('premium.employeeCost', 0);
                      } else {
                        formik.setFieldValue('premium.employeeCost', v);
                      }
                    }}
                  />
                </RowItem>
                <span style={{ alignSelf: 'end', marginBottom: '8px' }}>x</span>
                <RowItem>
                  <CommonCurrencyInput
                    id="employeeCostTimes"
                    label=""
                    name="employeeCostTimes"
                    placeholder="Please enter a number"
                    prefix="$"
                    decimalsLimit={7}
                    allowNegativeValue={false}
                    value={(formik.values.premium as ICoverageAmountBased).employeeCostTimes}
                    onValueChange={v => {
                      if (Number.isNaN(v)) {
                        formik.setFieldValue('premium.employeeCostTimes', 0);
                      } else {
                        formik.setFieldValue('premium.employeeCostTimes', v);
                      }
                    }}
                  />
                </RowItem>
                <RowLabel>
                  <Subtitle>{t('benefits.premiumConfig.spouseCost')}</Subtitle>
                  <span>{t('benefits.premiumConfig.coverageAmoundDivided')}</span>
                </RowLabel>
                <RowItem>
                  <CommonCurrencyInput
                    id="spouseCost"
                    label=""
                    name="spouseCost"
                    placeholder="Please enter a number"
                    prefix="$"
                    decimalsLimit={7}
                    allowNegativeValue={false}
                    value={(formik.values.premium as ICoverageAmountBased).spouseCost}
                    onValueChange={v => {
                      if (Number.isNaN(v)) {
                        formik.setFieldValue('premium.spouseCost', 0);
                      } else {
                        formik.setFieldValue('premium.spouseCost', v);
                      }
                    }}
                  />
                </RowItem>
                <span style={{ alignSelf: 'end', marginBottom: '8px' }}>x</span>
                <RowItem>
                  <CommonCurrencyInput
                    id="spouseCostTimes"
                    label=""
                    name="spouseCostTimes"
                    placeholder="Please enter a number"
                    prefix="$"
                    decimalsLimit={7}
                    allowNegativeValue={false}
                    value={(formik.values.premium as ICoverageAmountBased).spouseCostTimes}
                    onValueChange={v => {
                      if (Number.isNaN(v)) {
                        formik.setFieldValue('premium.spouseCostTimes', 0);
                      } else {
                        formik.setFieldValue('premium.spouseCostTimes', v);
                      }
                    }}
                  />
                </RowItem>
              </Row>

              <Row>
                <RowLabel>
                  <Subtitle>{t('benefits.premiumConfig.childCost')}</Subtitle>
                  <span>{t('benefits.premiumConfig.coverageAmoundDivided')}</span>
                </RowLabel>
                <RowItem>
                  <CommonCurrencyInput
                    id="childCost"
                    label="Coverage Amount"
                    name="childCost"
                    placeholder="Please enter a number"
                    prefix="$"
                    decimalsLimit={7}
                    allowNegativeValue={false}
                    value={(formik.values.premium as ICoverageAmountBased).childCost}
                    onValueChange={v => {
                      if (Number.isNaN(v)) {
                        formik.setFieldValue('premium.childCost', 0);
                      } else {
                        formik.setFieldValue('premium.childCost', v);
                      }
                    }}
                  />
                </RowItem>
                <span style={{ alignSelf: 'end', marginBottom: '8px' }}>x</span>
                <RowItem>
                  <CommonCurrencyInput
                    id="childCostTimes"
                    label=""
                    name="childCostTimes"
                    placeholder="Please enter a number"
                    prefix="$"
                    decimalsLimit={7}
                    allowNegativeValue={false}
                    value={(formik.values.premium as ICoverageAmountBased).childCostTimes}
                    onValueChange={v => {
                      if (Number.isNaN(v)) {
                        formik.setFieldValue('premium.childCostTimes', 0);
                      } else {
                        formik.setFieldValue('premium.childCostTimes', v);
                      }
                    }}
                  />
                </RowItem>
                <RowLabel>
                  <Subtitle>{t('benefits.premiumConfig.additionalAmountForSmokers')}</Subtitle>
                  <span>{t('benefits.premiumConfig.coverageAmoundDivided')}</span>
                </RowLabel>
                <RowItem>
                  <CommonCurrencyInput
                    id="additionalAmountForSmokers"
                    label=""
                    name="additionalAmountForSmokers"
                    placeholder="Please enter a number"
                    prefix="$"
                    decimalsLimit={7}
                    allowNegativeValue={false}
                    value={(formik.values.premium as ICoverageAmountBased).additionalAmountForSmokers}
                    onValueChange={v => {
                      if (Number.isNaN(v)) {
                        formik.setFieldValue('premium.additionalAmountForSmokers', 0);
                      } else {
                        formik.setFieldValue('premium.additionalAmountForSmokers', v);
                      }
                    }}
                  />
                </RowItem>
                <span style={{ alignSelf: 'end', marginBottom: '8px' }}>x</span>
                <RowItem>
                  <CommonCurrencyInput
                    id="additionalAmountForSmokersTimes"
                    label=""
                    name="additionalAmountForSmokersTimes"
                    placeholder="Please enter a number"
                    prefix="$"
                    decimalsLimit={7}
                    allowNegativeValue={false}
                    value={(formik.values.premium as ICoverageAmountBased).additionalAmountForSmokersTimes}
                    onValueChange={v => {
                      if (Number.isNaN(v)) {
                        formik.setFieldValue('premium.additionalAmountForSmokersTimes', 0);
                      } else {
                        formik.setFieldValue('premium.additionalAmountForSmokersTimes', v);
                      }
                    }}
                  />
                </RowItem>
              </Row>
              <Row>
                <RowLabel>
                  <Subtitle>{t('benefits.premiumConfig.minPremiumAmount')}</Subtitle>
                  <RowItem>
                    <CommonCurrencyInput
                      id="minimumPremiumAmount"
                      label=""
                      name="minimumPremiumAmount"
                      placeholder="Please enter a number"
                      prefix="$"
                      decimalsLimit={7}
                      allowNegativeValue={false}
                      value={(formik.values.premium as ICoverageAmountBased).minimumPremiumAmount}
                      onValueChange={v => {
                        if (Number.isNaN(v)) {
                          formik.setFieldValue('premium.minimumPremiumAmount', 0);
                        } else {
                          formik.setFieldValue('premium.minimumPremiumAmount', v);
                        }
                      }}
                    />
                  </RowItem>
                </RowLabel>
              </Row>
            </>
          )}
          <RadioContainer>
            <input
              type="radio"
              id="premium"
              name="premium"
              value="basedOnCoverageAmountAndAge"
              checked={formik.values.premiumType === 'basedOnCoverageAmountAndAge'}
              onChange={e => {
                formik.setFieldValue('premiumType', e.target.value);
              }}
            />
            <label>{t('benefits.premiumConfig.basedOnCoverageAmountAndAge')}</label>
          </RadioContainer>
          {formik.values.premiumType === 'basedOnCoverageAmountAndAge' && (
            <>
              <Row>
                <RowLabel>
                  <Subtitle>{t('benefits.premiumConfig.employeeCost')}</Subtitle>
                  <span>{t('benefits.premiumConfig.coverageAmoundDivided')}</span>
                </RowLabel>
                <RowItem>
                  <CommonCurrencyInput
                    id="employeeCost"
                    label=""
                    name="employeeCost"
                    placeholder="Please enter a number"
                    prefix="$"
                    decimalsLimit={7}
                    allowNegativeValue={false}
                    value={(formik.values.premium as IBasedOnCoverageAmountAndAge).employeeCost}
                    onValueChange={v => {
                      if (Number.isNaN(v)) {
                        formik.setFieldValue('premium.employeeCost', 0);
                      } else {
                        formik.setFieldValue('premium.employeeCost', v);
                      }
                    }}
                  />
                </RowItem>
                <RowItem style={{ marginRight: '40px' }}>
                  <div style={{ alignSelf: 'center', marginBottom: '8px' }}>x (based on age range)</div>
                </RowItem>

                <RowLabel>
                  <Subtitle>{t('benefits.premiumConfig.spouseCost')}</Subtitle>
                  <span>{t('benefits.premiumConfig.coverageAmoundDivided')}</span>
                </RowLabel>
                <RowItem>
                  <CommonCurrencyInput
                    id="spouseCost"
                    label=""
                    name="spouseCost"
                    placeholder="Please enter a number"
                    prefix="$"
                    decimalsLimit={7}
                    allowNegativeValue={false}
                    value={(formik.values.premium as IBasedOnCoverageAmountAndAge).spouseCost}
                    onValueChange={v => {
                      if (Number.isNaN(v)) {
                        formik.setFieldValue('premium.spouseCost', 0);
                      } else {
                        formik.setFieldValue('premium.spouseCost', v);
                      }
                    }}
                  />
                </RowItem>
                <RowItem>
                  <div style={{ alignSelf: 'center', marginBottom: '8px' }}>x (based on age range)</div>
                </RowItem>
              </Row>

              <Row>
                <RowLabel>
                  <Subtitle>{t('benefits.premiumConfig.childCost')}</Subtitle>
                  <span>{t('benefits.premiumConfig.coverageAmoundDivided')}</span>
                </RowLabel>
                <RowItem>
                  <div style={{ display: 'flex', gap: '15px', alignItems: 'end' }}>
                    <CommonCurrencyInput
                      id="childCost"
                      label=""
                      name="childCost"
                      placeholder="Please enter a number"
                      prefix="$"
                      decimalsLimit={7}
                      allowNegativeValue={false}
                      value={(formik.values.premium as IBasedOnCoverageAmountAndAge).childCost}
                      onValueChange={v => {
                        if (Number.isNaN(v)) {
                          formik.setFieldValue('premium.childCost', 0);
                        } else {
                          formik.setFieldValue('premium.childCost', v);
                        }
                      }}
                    />
                    <span style={{ alignSelf: 'end', marginBottom: '8px' }}>x</span>
                    <CommonCurrencyInput
                      id="childCostTimes"
                      label=""
                      name="childCostTimes"
                      placeholder="Please enter a number"
                      prefix="$"
                      decimalsLimit={7}
                      allowNegativeValue={false}
                      value={(formik.values.premium as IBasedOnCoverageAmountAndAge).childCostTimes}
                      onValueChange={v => {
                        if (Number.isNaN(v)) {
                          formik.setFieldValue('premium.childCostTimes', 0);
                        } else {
                          formik.setFieldValue('premium.childCostTimes', v);
                        }
                      }}
                    />
                  </div>
                </RowItem>
                <RowLabel>
                  <Subtitle>{t('benefits.premiumConfig.additionalAmountForSmokers')}</Subtitle>
                  <span>{t('benefits.premiumConfig.coverageAmoundDivided')}</span>
                </RowLabel>
                <RowItem>
                  <CommonCurrencyInput
                    id="additionalAmountForSmokers"
                    label=""
                    name="additionalAmountForSmokers"
                    placeholder="Please enter a number"
                    prefix="$"
                    decimalsLimit={7}
                    allowNegativeValue={false}
                    value={(formik.values.premium as IBasedOnCoverageAmountAndAge).additionalAmountForSmokers}
                    onValueChange={v => {
                      if (Number.isNaN(v)) {
                        formik.setFieldValue('premium.additionalAmountForSmokers', 0);
                      } else {
                        formik.setFieldValue('premium.additionalAmountForSmokers', v);
                      }
                    }}
                  />
                </RowItem>
                <RowItem>
                  <div style={{ alignSelf: 'center', marginBottom: '8px' }}>x (based on age range)</div>
                </RowItem>
              </Row>
              <Subtitle>{t('benefits.premiumConfig.dateLookingForAge')}</Subtitle>
              <Row>
                <RowItem>
                  <CommonDropdown
                    label="Open Enrollment"
                    id="openEnrollmentDateOption"
                    name="openEnrollmentDateOption"
                    setValue={(value: string) => {
                      formik.setFieldValue('premium.openEnrollmentDateOption', value);
                    }}
                    value={(formik.values.premium as IBasedOnCoverageAmountAndAge).openEnrollmentDateOption}
                    options={openEnrollmentDateDropdownOptions || []}
                    selectStyle={{
                      maxWidth: 'none',
                      width: '100%',
                      backgroundColor: '#ffffff',
                      height: '35px',
                      marginTop: '8px',
                    }}
                  />
                </RowItem>
                <RowItem>
                  <DatePicker
                    id="openEnrollmentDate"
                    name="openEnrollmentDate"
                    selected={handleSelectedDate(
                      (formik.values.premium as IBasedOnCoverageAmountAndAge).openEnrollmentDate
                    )}
                    dateFormat="MM/dd/yyyy"
                    onChange={date => formik.setFieldValue('premium.openEnrollmentDate', date)}
                    label="Date"
                    placeholderText="MM/DD/YYYY"
                  />
                </RowItem>
                <RowItem>
                  <CommonDropdown
                    label="New Hire & QLE"
                    id="newHireQLEDateOption"
                    name="newHireQLEDateOption"
                    setValue={(value: string) => {
                      formik.setFieldValue('premium.newHireQLEDateOption', value);
                    }}
                    value={(formik.values.premium as IBasedOnCoverageAmountAndAge).newHireQLEDateOption}
                    options={qleDateDropdownOptions || []}
                    selectStyle={{
                      maxWidth: 'none',
                      width: '100%',
                      backgroundColor: '#ffffff',
                      height: '35px',
                      marginTop: '8px',
                    }}
                  />
                </RowItem>
                <RowItem>
                  <DatePicker
                    id="newHireQLEDate"
                    name="newHireQLEDate"
                    selected={handleSelectedDate(
                      (formik.values.premium as IBasedOnCoverageAmountAndAge).newHireQLEDate
                    )}
                    dateFormat="MM/dd/yyyy"
                    onChange={date => formik.setFieldValue('premium.newHireQLEDate', date)}
                    label="Date"
                    placeholderText="MM/DD/YYYY"
                  />
                </RowItem>
                <StyledRadioContainer>
                  <span>{t('benefits.premiumConfig.useEmployeeAgeForSpousePremium')}</span>

                  <StyledRadioGroup>
                    <StyledRadioButton>
                      <StyledRadioInput
                        type="radio"
                        id="smoke1"
                        name="smoke"
                        value="true"
                        checked={(formik.values.premium as IBasedOnCoverageAmountAndAge).useEmployeeAgeForSpousePremium}
                        onChange={() => {
                          formik.setFieldValue('premium.useEmployeeAgeForSpousePremium', true);
                        }}
                      />
                      <StyledRadioLabel>{t('global.form.yes')}</StyledRadioLabel>
                    </StyledRadioButton>

                    <StyledRadioButton>
                      <StyledRadioInput
                        type="radio"
                        id="smoke2"
                        name="smoke"
                        value="false"
                        checked={
                          !(formik.values.premium as IBasedOnCoverageAmountAndAge).useEmployeeAgeForSpousePremium
                        }
                        onChange={() => {
                          formik.setFieldValue('premium.useEmployeeAgeForSpousePremium', false);
                        }}
                      />
                      <StyledRadioLabel>{t('global.form.no')}</StyledRadioLabel>
                    </StyledRadioButton>
                  </StyledRadioGroup>
                </StyledRadioContainer>
              </Row>
              <Row>
                <RowLabel>
                  <Subtitle>{t('benefits.premiumConfig.minPremiumAmount')}</Subtitle>
                  <RowItem>
                    <CommonCurrencyInput
                      id="minimumPremiumAmount"
                      label=""
                      name="minimumPremiumAmount"
                      placeholder="Please enter a number"
                      prefix="$"
                      decimalsLimit={7}
                      allowNegativeValue={false}
                      value={(formik.values.premium as ICoverageAmountBased).minimumPremiumAmount}
                      onValueChange={v => {
                        if (Number.isNaN(v)) {
                          formik.setFieldValue('premium.minimumPremiumAmount', 0);
                        } else {
                          formik.setFieldValue('premium.minimumPremiumAmount', v);
                        }
                      }}
                    />
                  </RowItem>
                </RowLabel>
              </Row>
            </>
          )}
        </RadioBody>
      </Row>
      <Subtitle>{t('benefits.premiumConfig.employerProvidedQuantityType')}</Subtitle>
      <Row>
        <RadioBody>
          <RadioContainer>
            <input
              type="radio"
              id="employerQuantityType"
              name="employerQuantityType"
              value="nothing"
              checked={formik.values.employerProvidedQuantityType === 'nothing'}
              onChange={e => {
                formik.setFieldValue('employerProvidedQuantityType', e.target.value);
              }}
            />
            <label>{t('benefits.premiumConfig.nothing')}</label>
          </RadioContainer>
          <RadioContainer>
            <input
              type="radio"
              id="employerQuantityType"
              name="employerQuantityType"
              value="fixed"
              checked={formik.values.employerProvidedQuantityType === 'fixed'}
              onChange={e => {
                formik.setFieldValue('employerProvidedQuantityType', e.target.value);
              }}
            />
            <label>{t('benefits.premiumConfig.fixedPortion')}</label>
          </RadioContainer>
          {formik.values.employerProvidedQuantityType === 'fixed' && (
            <Row>
              <RowItem>
                <CommonCurrencyInput
                  id="employeeAssistance"
                  label="Employer Assistance"
                  name="employeeAssistance"
                  placeholder="Please enter a number"
                  prefix="$"
                  decimalsLimit={7}
                  allowNegativeValue={false}
                  value={
                    (formik.values.employerProvidedQuantity as IFixedPortionOfTheEmployeePremium).employerAssistance
                  }
                  onValueChange={v => {
                    if (Number.isNaN(v)) {
                      formik.setFieldValue('employerProvidedQuantity.employerAssistance', 0);
                    } else {
                      formik.setFieldValue('employerProvidedQuantity.employerAssistance', v);
                    }
                  }}
                />
              </RowItem>
            </Row>
          )}
          <RadioContainer>
            <input
              type="radio"
              id="employerQuantityType"
              name="employerQuantityType"
              value="percentage"
              checked={formik.values.employerProvidedQuantityType === 'percentage'}
              onChange={e => {
                formik.setFieldValue('employerProvidedQuantityType', e.target.value);
              }}
            />
            <label>{t('benefits.premiumConfig.percentageOfPremium')}</label>
          </RadioContainer>
          {formik.values.employerProvidedQuantityType === 'percentage' && (
            <Row>
              <RowItem>
                <CommonCurrencyInput
                  id="employeeAssistance"
                  label="Employer Assistance"
                  name="employeeAssistance"
                  placeholder="Please enter a number"
                  suffix="%"
                  allowDecimals={false}
                  allowNegativeValue={false}
                  value={
                    (formik.values.employerProvidedQuantity as IFixedPortionOfTheEmployeePremium).employerAssistance
                  }
                  onValueChange={v => {
                    if (Number.isNaN(v)) {
                      formik.setFieldValue('employerProvidedQuantity.employerAssistance', 0);
                    } else {
                      formik.setFieldValue('employerProvidedQuantity.employerAssistance', v);
                    }
                  }}
                />
              </RowItem>
            </Row>
          )}

          <RadioContainer>
            <input
              type="radio"
              id="employerQuantityType"
              name="employerQuantityType"
              value="full"
              checked={formik.values.employerProvidedQuantityType === 'full'}
              onChange={e => {
                formik.setFieldValue('employerProvidedQuantityType', e.target.value);
              }}
            />
            <label>{t('benefits.premiumConfig.fullPremium')}</label>
          </RadioContainer>
        </RadioBody>
      </Row>
    </Container>
  );
};
